// views/Tarefas/DirecionarSolicitacaoModal.tsx
//
// Miniformulário "Direcionar" usado só dentro do SolicitacaoDetailModal (aberto pelo botão
// "Direcionar"/"Trocar" ao lado do Responsável). NÃO é o mesmo modal de Transferir usado nas
// linhas do Kanban/Tabela/Lista da Central de Solicitações — aquele continua em TarefasView.tsx
// e serve tarefa comum e solicitação juntos. Separado de propósito: mexer no de lá arriscava um
// fluxo já em produção pra um ganho pequeno de reuso.
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Profile, Tarefa } from '../../types';
import { daEquipeDaSolicitacao, podeEntrarNaCentral, podeReceberPedido } from './permissoesSolicitacao';
import DatePicker from '../../src/components/DatePicker';

interface DirecionarSolicitacaoModalProps {
  isOpen: boolean;
  task: Tarefa | null;
  users: Profile[];
  onClose: () => void;
  onSubmit: (responsavelId: string, dueDate: string) => Promise<void>;
}

const DirecionarSolicitacaoModal: React.FC<DirecionarSolicitacaoModalProps> = ({ isOpen, task, users, onClose, onSubmit }) => {
  const [responsavelId, setResponsavelId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Reseta o formulário sempre que o modal abre de novo (evita herdar seleção da última vez)
  useEffect(() => {
    if (isOpen) {
      setResponsavelId('');
      setDueDate('');
    }
  }, [isOpen]);

  if (!isOpen || !task) return null;

  // #6518: quem pode receber é a equipe que atende ESTA solicitação (antes era sempre Tecnologia/Marketing),
  // e só quem consegue abrir a Central.
  const elegiveis = users.filter(u => u.status !== 'inactive' && daEquipeDaSolicitacao(task, u) && podeEntrarNaCentral(u) && podeReceberPedido(u));

  const handleSubmit = async () => {
    if (!responsavelId) return;
    setSaving(true);
    try {
      await onSubmit(responsavelId, dueDate);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">Direcionar Solicitação</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">Selecione o novo responsável para: <strong>{task.titulo}</strong></p>
        <select
          aria-label="Novo responsável"
          className={`w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 ${elegiveis.length === 0 ? 'mb-1' : 'mb-4'}`}
          value={responsavelId}
          onChange={e => setResponsavelId(e.target.value)}
        >
          <option value="">Selecione...</option>
          {elegiveis.map(u => (
            <option key={u.id} value={u.id}>{u.full_name}</option>
          ))}
        </select>
        {elegiveis.length === 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-4">Não há ninguém ativo na equipe que atende esta solicitação. Confira o departamento no cadastro das pessoas.</p>
        )}
        <div className="mb-6">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
            Prazo de entrega <span className="text-gray-400 font-normal">(opcional, dá pra ajustar depois)</span>
          </label>
          <DatePicker value={dueDate} onChange={setDueDate} placeholder="Sem prazo definido" />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm font-medium disabled:opacity-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={!responsavelId || saving} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
            {saving ? 'Direcionando...' : 'Direcionar'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DirecionarSolicitacaoModal;
